import React, { useState, useEffect } from 'react';
import { Table, Typography, Spin, notification, Button, Modal, Form, Input, InputNumber, DatePicker, Popconfirm } from 'antd';
import { saveAs } from 'file-saver';
import Papa from 'papaparse'; // Import PapaParse
import moment from 'moment';

const { Title } = Typography;

const Home = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalInvoiceAmount, setTotalInvoiceAmount] = useState(0);
  const [totalStockUsed, setTotalStockUsed] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [form] = Form.useForm();
  const [productOptions, setProductOptions] = useState([]);
  const [totalRemaining, setTotalRemaining] = useState(0);

  const [gstPercentage, setGstPercentage] = useState(0);  // Replace advance with gst_percentage

  useEffect(() => {
    fetchInvoices();
    fetchProducts();
  }, []);
  useEffect(() => {
    const filtered = invoices.filter(invoice =>
      invoice.customer_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredInvoices(filtered);

    // Calculate total gst and remaining amounts for filtered invoices
    const gstAmount = filtered.reduce((sum, invoice) => {
      const totalAmount = parseFloat(invoice.total_amount || 0);
      const gstPercentage = parseFloat(invoice.gst_percentage || 0);
      return sum + (totalAmount * gstPercentage / 100); // Calculate GST based on gst_percentage
    }, 0);

    const remaining = filtered.reduce((sum, invoice) => {
      const totalAmount = parseFloat(invoice.total_amount || 0);
      const gstAmount = (totalAmount * (parseFloat(invoice.gst_percentage || 0) / 100));
      return sum + (totalAmount - gstAmount);
    }, 0);

    const totalAmount = filtered.reduce((sum, invoice) => {
      return sum + (parseFloat(invoice.total_amount || 0));
    }, 0);

    setGstPercentage(gstAmount);  // Rename variable to 'gstAmount'
    setTotalRemaining(remaining);
    setTotalInvoiceAmount(totalAmount);
  }, [searchQuery, invoices]);

  const fetchProducts = async () => {
    try {
      const response = await fetch('https://chalan.eoptilab.com/backend/get_product.php');
      const result = await response.json();
      if (result.success) {
        setProductOptions(result.data);
      } else {
        notification.error({ message: 'Error', description: result.message });
      }
    } catch (error) {
      notification.error({ message: 'Error', description: 'Failed to fetch products' });
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await fetch('https://chalan.eoptilab.com/backend/get_invoice.php');
      const result = await response.json();

      if (result.success) {
        const data = result.data;
        setInvoices(data);

        const invoiceAmount = data.reduce((sum, invoice) => {
          const totalAmount = parseFloat(invoice.total_amount || 0);
          const discount = parseFloat(invoice.discount || 0);
          return sum + (totalAmount - (totalAmount * (discount / 100)));
        }, 0);

        const stockUsed = data.reduce((sum, invoice) => {
          return sum + (invoice.items ? invoice.items.reduce((productSum, product) => productSum + (product.quantity || 0), 0) : 0);
        }, 0);

        setTotalInvoiceAmount(invoiceAmount);
        setTotalStockUsed(stockUsed);
      } else {
        notification.error({ message: 'Error', description: result.message });
      }
    } catch (error) {
      notification.error({ message: 'Error', description: 'Failed to fetch invoices' });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'Invoice Number', dataIndex: 'invoice_number', key: 'invoice_number' },
    { title: 'Customer', dataIndex: 'customer_name', key: 'customer_name' },
    { title: 'Date', dataIndex: 'created_at', key: 'created_at', render: date => moment(date).format('YYYY-MM-DD') },
    { title: 'Total Amount', dataIndex: 'total_amount', key: 'total_amount', render: amount => `₹${parseFloat(amount || 0).toFixed(2)}` },

,
      { title: 'Remaining Payment', key: 'remaining_payment', render: (text, record) => {
        const totalAmount = parseFloat(record.total_amount || 0);
        const advanceAmount = parseFloat(record.advance || 0);
        return `₹${(totalAmount - advanceAmount).toFixed(2)}`;
    }},
    { title: 'Products', dataIndex: 'items', key: 'items', render: items => items ? items.map(item => <div key={item.id}>{item.product_name}: {item.quantity}</div>) : null },
    { title: 'Action', key: 'action', render: (text, record) => (
      <div>
        {/* <Button onClick={() => editInvoice(record)} style={{ marginRight: 8 }}>Edit</Button> */}
        <Popconfirm title="Are you sure you want to delete this invoice?" onConfirm={() => deleteInvoice(record.id)} okText="Yes" cancelText="No">
          <Button type="danger">Delete</Button>
        </Popconfirm>
      </div>
    ) },
  ];

  const editInvoice = (invoice) => {
    setEditingInvoice(invoice);
    form.setFieldsValue({
      invoiceNumber: invoice.invoice_number,
      customer: invoice.customer_name,
      state: invoice.state,
      address: invoice.address || '',
      number: invoice.phone_number || '',
      invoiceDate: moment(invoice.created_at),
      discount: invoice.discount || 0,
      gstAmount: invoice.gstAmount || 0,
      defaultDiscount: invoice.default_discount || 0,
      category: invoice.category || '',
      forInvoiceNo: invoice.for_invoice_no || '',
      froInvoiceAmount: invoice.fro_invoice_amount || 0,
      products: invoice.items ? invoice.items.map(item => ({
        key: item.id,
        product: item.product_name,
        quantity: item.quantity,
        price: item.price
      })) : [],
      narration: invoice.narration || '',
    });
  };
  const handleUpdate = async (values) => {
    try {
      // Calculate total amount and GST for products
      const productsTotal = (values.products || []).reduce((sum, product) => {
        return sum + (product.quantity * product.price);
      }, 0);

      const totalAmount = productsTotal + values.gstAmount - (values.defaultDiscount || 0);

      const response = await fetch('https://chalan.eoptilab.com/backend/update_invoice.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoiceId: editingInvoice.id,
          invoiceNumber: values.invoiceNumber,
          customer: values.customer,
          state: values.state,
          address: values.address,
          number: values.number,
          invoiceDate: values.invoiceDate.format('YYYY-MM-DD'),
          gstAmount: values.gstAmount,
          defaultDiscount: values.defaultDiscount || 0,
          category: values.category,
          forInvoiceNo: values.forInvoiceNo,
          froInvoiceAmount: values.froInvoiceAmount || 0,
          products: values.products || [],
          narration: values.narration,
          totalAmount: totalAmount
        }),
      });

      const result = await response.json();

      if (result.success) {
        notification.success({ message: 'Success', description: 'Invoice updated successfully' });
        setEditingInvoice(null);
        form.resetFields();
        fetchInvoices();
      } else {
        notification.error({ message: 'Error', description: result.message });
      }
    } catch (error) {
      notification.error({ message: 'Error', description: 'Failed to update invoice' });
    }
  };

  const deleteInvoice = async (id) => {
    try {
      const response = await fetch('https://chalan.eoptilab.com/backend/delete_invoice.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id }),
      });

      const result = await response.json();

      if (result.success) {
        notification.success({ message: 'Success', description: 'Invoice deleted successfully' });
        fetchInvoices(); // Refetch invoices to reflect changes
      } else {
        notification.error({ message: 'Error', description: result.message });
      }
    } catch (error) {
      notification.error({ message: 'Error', description: 'Failed to delete invoice' });
    }
  };

  const downloadCSV = () => {
    const csvData = filteredInvoices.map(invoice => ({
      'Invoice Number': invoice.invoice_number,
      'Customer': invoice.customer_name,
      'Date': moment(invoice.created_at).format('YYYY-MM-DD'),
      'Total Amount': `₹${parseFloat(invoice.total_amount || 0).toFixed(2)}`,
      'GST Amount': `₹${parseFloat(invoice.advance || 0).toFixed(2)}`,
      'Remaining Payment': `₹${(parseFloat(invoice.total_amount || 0) - parseFloat(invoice.advance || 0)).toFixed(2)}`,
      'Products': invoice.items ? invoice.items.map(item => `${item.product_name}: ${item.quantity}`).join(', ') : '',
    }));

    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'invoices.csv');
  };
const printInvoice = () => {
    if (!editingInvoice) return;
    const discount = parseFloat(editingInvoice.discount) || 0;
    const advance = parseFloat(editingInvoice.advance) || 0;
    const totalAmount = parseFloat(editingInvoice.total_amount) || 0;
    const invoiceHtml = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Invoice</title>
        <style>
            @page {
                size: A4;
                margin: 20mm;
            }
            body {
                font-family: Arial, sans-serif;
                color: #333;
                margin: 0;
                padding: 0;
                box-sizing: border-box;
                background-color: #f9f9f9;
            }
            .container {
                width: 100%;
                max-width: 800px;
                margin: 0 auto;
                padding: 20px;
                border: 1px solid #ccc;
                background-color: #fff;
                border-radius: 8px;
                box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
                position: relative;
            }
            .header {
                text-align: center;
                margin-bottom: 20px;
                border-bottom: 2px solid #333;
                padding-bottom: 10px;
            }
            .header img {
                max-width: 150px;
            }
            .header h1 {
                margin: 0;
                font-size: 26px;
                text-transform: uppercase;
                color: #333;
            }
                .company-details, .invoice-details {
                margin-bottom: 20px;
                font-size: 14px;
            }
            .company-details p, .invoice-details p {
                margin: 5px 0;
            }
            .company-details {
                border-bottom: 1px solid #ddd;
                padding-bottom: 10px;
            }
                .invoice-details {
                border-bottom: 1px solid #ddd;
                padding-bottom: 10px;
            }
            .items-table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 20px;
                font-size: 12px;
            }
            .items-table th, .items-table td {
                padding: 10px;
                text-align: left;
                border: 1px solid #ddd;
            }
            .items-table th {
                background-color: #f4f4f4;
                font-weight: bold;
            }
            .total-row {
                font-weight: bold;
                text-align: right;
                border-top: 2px solid #333;
            }
            .footer {
                font-size: 12px;
                text-align: center;
                margin-top: 20px;
                padding-top: 10px;
                border-top: 1px solid #ddd;
            }
            .footer p {
                margin: 5px 0;
                color: #666;
            }
            .amount-words {
                font-style: italic;
                font-size: 12px;
                margin-top: 10px;
                text-align: left;
            }
            .signatory {
                margin-top: 40px;
                text-align: right;
                font-size: 12px;
            }
            .qr-code {
                position: absolute;
                bottom: 20px;
                left: 20px;
                max-width: 100px;
            }
        </style>
    </head>
    <body>
        <div class="container">
            <!-- Header with Company Logo and Title -->
            <div class="header">
                <h1>Challan</h1>
            </div>


           <div class="invoice-details">

                <p><strong>Challan Number:</strong> ${editingInvoice.invoice_number || 'N/A'}</p>
                <p><strong>Date:</strong> ${moment(editingInvoice.created_at).format('DD-MMM-YY') || 'N/A'}</p>
                <p><strong>Customer:</strong> ${editingInvoice.customer_name}</p>
                <p><strong>Address:</strong> ${editingInvoice.address || ''}</p>
                <p><strong>State:</strong> ${editingInvoice.state || ''}</p>
                <p><strong>Phone Number:</strong> ${editingInvoice.phone_number || ''}</p>
                <p><strong>Narration:</strong> ${editingInvoice.narration || 'No Narrations'}</p>
            </div>

            <!-- Items Table -->
            <table class="items-table">
                <thead>
                    <tr>
                        <th>Sl No.</th>
                        <th>Description of Goods</th>
                        <th>Quantity</th>
                        <th>Rate</th>
                        <th>Discount</th>
                        <th>GST Amount</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    ${editingInvoice.items ? editingInvoice.items.map((item, index) => {
                        const price = parseFloat(item.price) || 0;
                        const quantity = parseInt(item.quantity, 10) || 0;
                        const total = (quantity * price).toFixed(2);
                        return `
                            <tr>
                                <td>${index + 1}</td>
                                <td>${item.product_name || ''}</td>
                                <td>${quantity} ${item.unit || ''}</td>
                                <td>${price.toFixed(2)}</td>
                                <td>${editingInvoice.default_discount || ''}</td>
<td>${(parseFloat(item.price) * parseInt(item.quantity, 10) * (parseFloat(editingInvoice.gst_percentage || 0) / 100)).toFixed(2)}</td> <!-- GST Amount -->
                                <td>${total}</td>
                            </tr>
                        `;
                    }).join('') : ''}
                    <tr class="total-row">
                        <td colspan="4">Total</td>
                        <td>${parseFloat(totalAmount || 0).toFixed(2)}</td>
                    </tr>
                </tbody>
            </table>


              <!-- Footer -->
              <div class="footer">
                  <p>This is a Computer Generated Invoice</p>
              </div>

          </div>
    </body>
    </html>
    `;

    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.open();
    printWindow.document.write(invoiceHtml);
    printWindow.document.close();
    printWindow.print();
};


return (
  <div>
    <Title level={2}>Challan</Title>
    <Button type="primary" onClick={downloadCSV} style={{ marginBottom: 16 }}>Download CSV</Button>
    <Input.Search
      placeholder="Search by customer name"
      value={searchQuery}
      onChange={e => setSearchQuery(e.target.value)}
      style={{ marginBottom: 16 }}
    />
    {loading ? <Spin /> : <Table columns={columns} dataSource={filteredInvoices} rowKey="id" />}
    <Modal
      title="Edit Invoice"
      visible={!!editingInvoice}
      onCancel={() => setEditingInvoice(null)}
      footer={[
        <Button key="print" type="primary" onClick={printInvoice}>Print</Button>,
        <Button key="submit" type="primary" onClick={() => form.submit()}>
          {editingInvoice ? 'Update' : 'Create'}
        </Button>,
        <Button key="cancel" onClick={() => setEditingInvoice(null)}>Cancel</Button>
      ]}
    >
    <Form
    form={form}
    layout="vertical"
    onFinish={handleUpdate}
  >
    <Form.Item name="invoiceNumber" label="Invoice Number" rules={[{ required: true }]}>
      <Input />
    </Form.Item>
    <Form.Item name="customer" label="Customer" rules={[{ required: true }]}>
      <Input />
    </Form.Item>
    <Form.Item name="state" label="State">
      <Input />
    </Form.Item>
    <Form.Item name="address" label="Address">
      <Input />
    </Form.Item>
    <Form.Item name="number" label="Phone Number">
      <Input />
    </Form.Item>
    <Form.Item name="invoiceDate" label="Date" rules={[{ required: true }]}>
      <DatePicker format="YYYY-MM-DD" />
    </Form.Item>
    <Form.Item name="gstAmount" label="GST Amount" rules={[{ required: true }]}>
      <InputNumber min={0} step={0.01} />
    </Form.Item>
    <Form.Item name="defaultDiscount" label="Default Discount">
      <InputNumber min={0} step={0.01} />
    </Form.Item>
    <Form.Item name="category" label="Category">
      <Input />
    </Form.Item>
    <Form.Item name="forInvoiceNo" label="For Invoice No">
      <Input />
    </Form.Item>
    <Form.Item name="froInvoiceAmount" label="For Invoice Amount">
      <InputNumber min={0} step={0.01} />
    </Form.Item>
    <Form.Item name="narration" label="Narration">
      <Input.TextArea />
    </Form.Item>

    {/* Product form fields */}
    <Form.Item name="products" label="Products">
      <Form.List name="products">
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, fieldKey, ...restField }) => (
              <div key={key} style={{ marginBottom: 16, border: '1px solid #d9d9d9', padding: 16, borderRadius: 4 }}>
                <Form.Item
                  {...restField}
                  label="Product"
                  name={[name, 'product']}
                  fieldKey={[fieldKey, 'product']}
                  rules={[{ required: true, message: 'Missing product' }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  {...restField}
                  label="Quantity"
                  name={[name, 'quantity']}
                  fieldKey={[fieldKey, 'quantity']}
                  rules={[{ required: true, message: 'Missing quantity' }]}
                >
                  <InputNumber min={0} />
                </Form.Item>
                <Form.Item
                  {...restField}
                  label="Price"
                  name={[name, 'price']}
                  fieldKey={[fieldKey, 'price']}
                  rules={[{ required: true, message: 'Missing price' }]}
                >
                  <InputNumber min={0} step={0.01} />
                </Form.Item>
                <Button type="danger" onClick={() => remove(name)}>Remove</Button>
              </div>
            ))}
            <Form.Item>
              <Button type="dashed" onClick={() => add()} block>
                Add Product
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>
    </Form.Item>
  </Form>
    </Modal>
    <div>
      <p>Total Invoice Amount: ₹{totalInvoiceAmount.toFixed(2)}</p>
      <p>Total Stock Used: {totalStockUsed}</p>
      <p>Total Remaining: ₹{totalRemaining.toFixed(2)}</p>
    </div>
  </div>
);

};

export default Home;
